/**
 * Mobile native share helpers (Web Share API) with desktop/custom-popup fallback.
 * Cart order WhatsApp uses package-targeted Android intents or whatsapp:// — not the general share sheet.
 */

export const WHATSAPP_PACKAGE = 'com.whatsapp';
export const WHATSAPP_BUSINESS_PACKAGE = 'com.whatsapp.w4b';

/**
 * Detect phones/tablets (including iPadOS desktop-UA + coarse pointer).
 * Desktop browsers keep the custom share popup.
 */
export function isMobileDevice() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const ua = navigator.userAgent || '';
  const mobileUa =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(ua);
  // iPadOS 13+ may report as Macintosh
  const iPadOs = navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1;
  const coarsePointer = Boolean(
    window.matchMedia && window.matchMedia('(pointer: coarse)').matches
  );

  return mobileUa || iPadOs || coarsePointer;
}

export function isAndroidDevice() {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent || '');
}

/**
 * PWA/browser cannot reliably detect which WhatsApp apps are installed.
 * On Android we can target packages via Intent, so we offer an in-app WA vs WA Business
 * choice (not the OS share sheet). Elsewhere, package targeting is unsupported.
 */
export function shouldOfferWhatsAppAppChoice() {
  return isAndroidDevice();
}

/** Same title/text/url shape used by the custom Share popup. */
export function buildSharePayload({ name, url } = {}) {
  const title = name ? `Check out this: ${name}` : (url || 'Faster Link');
  return {
    title,
    text: title,
    url: url || undefined,
  };
}

/**
 * Attempt navigator.share.
 * @returns {'shared' | 'aborted' | 'unavailable'}
 */
export async function tryNativeShare(shareData) {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
    return 'unavailable';
  }

  try {
    if (typeof navigator.canShare === 'function' && !navigator.canShare(shareData)) {
      return 'unavailable';
    }
    await navigator.share(shareData);
    return 'shared';
  } catch (err) {
    // User dismissed the sheet — do not open the custom popup
    if (err?.name === 'AbortError') {
      return 'aborted';
    }
    return 'unavailable';
  }
}

/**
 * Mobile + Web Share API → native sheet.
 * Desktop, unsupported API, or share failure (not cancel) → openPopup().
 *
 * @returns {'shared' | 'aborted' | 'fallback'}
 */
export async function shareWithNativeOrPopup({ name, url, openPopup }) {
  const payload = buildSharePayload({ name, url });

  if (isMobileDevice()) {
    const result = await tryNativeShare(payload);
    if (result === 'shared' || result === 'aborted') {
      return result;
    }
  }

  openPopup?.();
  return 'fallback';
}

/** Digits only, keep country code; strip spaces, +, dashes, etc. */
export function normalizeWhatsAppPhone(phoneNumber) {
  if (phoneNumber == null) return '';
  let raw = phoneNumber;
  if (typeof raw === 'object') {
    raw = raw.phone || raw.number || raw.whatsapp || raw.whatss || '';
  }
  return String(raw).replace(/\D/g, '');
}

/**
 * @param {'whatsapp' | 'business' | null | undefined} app
 * @returns {string | null} Android package, or null when not targeting a package
 */
export function resolveWhatsAppPackage(app) {
  if (app === 'business') return WHATSAPP_BUSINESS_PACKAGE;
  if (app === 'whatsapp') return WHATSAPP_PACKAGE;
  return null;
}

/**
 * Build a deep link for a specific WhatsApp app when possible.
 * Android + app choice → intent:// with package (avoids wa.me defaulting to Business).
 * Otherwise → whatsapp:// then callers may fall back to https://wa.me.
 */
export function buildWhatsAppOrderUrl({ phoneNumber, message, app } = {}) {
  const digits = normalizeWhatsAppPhone(phoneNumber);
  if (!digits) return null;

  const text = encodeURIComponent(message || '');
  const sendPath = `send/?phone=${digits}&text=${text}`;
  const pkg = resolveWhatsAppPackage(app);

  if (isAndroidDevice() && pkg) {
    // Force the chosen package — do not use wa.me (OS may auto-open Business).
    return `intent://${sendPath}#Intent;scheme=whatsapp;package=${pkg};end`;
  }

  return `whatsapp://send?phone=${digits}&text=${text}`;
}

export function buildWhatsAppHttpsFallback({ phoneNumber, message } = {}) {
  const digits = normalizeWhatsAppPhone(phoneNumber);
  if (!digits) return null;
  return `https://wa.me/${digits}?text=${encodeURIComponent(message || '')}`;
}

function navigateToUrl(url) {
  try {
    // Prefer location for intent:// — window.open is unreliable for custom schemes
    if (String(url).startsWith('intent:')) {
      window.location.href = url;
      return true;
    }
    const opened = window.open(url, '_blank', 'noopener,noreferrer');
    if (!opened && typeof window.location?.assign === 'function') {
      window.location.assign(url);
    }
    return true;
  } catch (_) {
    try {
      window.location.href = url;
      return true;
    } catch (err2) {
      return false;
    }
  }
}

/**
 * Cart order → open store WhatsApp chat with optional app target.
 * Never uses navigator.share (no Telegram/Gmail/etc.).
 *
 * @param {{ message?: string, phoneNumber?: string, app?: 'whatsapp' | 'business' | null }} opts
 * @returns {{ ok: boolean, reason?: string, url?: string }}
 */
export function openStoreWhatsAppOrder({ message, phoneNumber, app } = {}) {
  const digits = normalizeWhatsAppPhone(phoneNumber);
  if (!digits) {
    return { ok: false, reason: 'missing_phone' };
  }

  const primary = buildWhatsAppOrderUrl({ phoneNumber: digits, message, app });
  if (!primary) {
    return { ok: false, reason: 'missing_phone' };
  }

  if (!navigateToUrl(primary)) {
    const httpsFallback = buildWhatsAppHttpsFallback({ phoneNumber: digits, message });
    if (httpsFallback && navigateToUrl(httpsFallback)) {
      return { ok: true, url: httpsFallback };
    }
    return { ok: false, reason: 'open_failed' };
  }

  // Non-Android / no package: if whatsapp:// is unsupported, try https shortly after
  if (!isAndroidDevice() || !resolveWhatsAppPackage(app)) {
    const httpsFallback = buildWhatsAppHttpsFallback({ phoneNumber: digits, message });
    if (httpsFallback) {
      window.setTimeout(() => {
        // Only fall back if we likely stayed in the browser (app open would hide page)
        if (document.visibilityState === 'visible') {
          navigateToUrl(httpsFallback);
        }
      }, 1200);
    }
  }

  return { ok: true, url: primary };
}
